import { GMetrixAccordion, GMetrixAccordionItem } from 'components/GMetrixAccordion/GMetrixAccordion';
import type { AssignableItem } from 'pages/custom_tests_v1/components/assign_wizard/AssignWizard';
import { useAssignWizard } from 'pages/custom_tests_v1/components/assign_wizard/context';
import { Col, ListGroup, Row } from 'react-bootstrap';
import { useIntl } from 'react-intl';

type ChangeGroupProps = {
    emptyText: string;

    // `expanded` is read by GMetrixAccordion to decide the default-open item; `eventKey` is injected by it.
    eventKey?: string;
    expanded?: boolean;
    items: AssignableItem[];
    title: string;
    variant: 'success' | 'secondary' | 'danger';
};

const ChangeGroup = (props: ChangeGroupProps) => {

    const { emptyText, eventKey, items, title, variant } = props;

    return (
        <GMetrixAccordionItem
            title={(
                <span className={'d-flex align-items-center gap-2'}>
                    <span className={`badge bg-${variant}`}>
                        {items.length}
                    </span>
                    <span className={'fw-semibold'}>
                        {title}
                    </span>
                </span>
            )}
            eventKey={eventKey}
        >
            {items.length === 0 ?
                (
                    <div className={'text-muted fst-italic small'}>
                        {emptyText}
                    </div>
                )
                :
                (
                    <ListGroup>
                        {items.map(({ Id, Identifier, Name }) => (
                            <ListGroup.Item className={`fw-semibold border-start border-3 border-${variant}`} key={Id}>
                                {Name || Identifier}
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                )
            }
        </GMetrixAccordionItem>
    );

};

export const ReviewStep = () => {

    const { initialSelectedAccessCodes, initialSelectedClassrooms, selectedAccessCodes, selectedClassrooms, testName } = useAssignWizard();
    const { formatMessage } = useIntl();

    const assignedAccessCodeIds = initialSelectedAccessCodes.map(({ Id }) => Id);
    const assignedClassroomIds = initialSelectedClassrooms.map(({ Id }) => Id);

    const selectedAccessCodeIds = selectedAccessCodes.map(({ Id }) => Id);
    const selectedClassroomIds = selectedClassrooms.map(({ Id }) => Id);

    const addedAccessCodes = selectedAccessCodes.filter(({ Id }) => !assignedAccessCodeIds.includes(Id));
    const keptAccessCodes = selectedAccessCodes.filter(({ Id }) => assignedAccessCodeIds.includes(Id));
    const removedAccessCodes = initialSelectedAccessCodes.filter(({ Id }) => !selectedAccessCodeIds.includes(Id));

    const addedClassrooms = selectedClassrooms.filter(({ Id }) => !assignedClassroomIds.includes(Id));
    const keptClassrooms = selectedClassrooms.filter(({ Id }) => assignedClassroomIds.includes(Id));
    const removedClassrooms = initialSelectedClassrooms.filter(({ Id }) => !selectedClassroomIds.includes(Id));

    const hasChanges = addedAccessCodes.length > 0 || removedAccessCodes.length > 0
        || addedClassrooms.length > 0 || removedClassrooms.length > 0;

    return (
        <div>
            <h6 className={'mb-3'}>
                {formatMessage({ id: 'review_and_assign.f9bf4263', defaultMessage: 'Review & Assign' })}
            </h6>

            <div className={'card card-body mb-3'}>
                <div>
                    <strong>
                        {formatMessage({ id: 'test_name.77ea159f', defaultMessage: 'Test Name' })}
                        :
                    </strong>
                    <span className={'ms-2'}>
                        {testName}
                    </span>
                </div>
            </div>

            {hasChanges ?
                (
                    <p className={'text-muted'}>
                        {formatMessage({ id: 'review_the_changes_below_green_items_wil.2f66ac54', defaultMessage: 'Review the changes below. Green items will be newly assigned, red items will be unassigned, and grey items stay assigned.' })}
                    </p>
                )
                :
                (
                    <div className={'alert alert-info'}>
                        {formatMessage({ id: 'no_changes_to_apply.b1abe7a1', defaultMessage: 'No changes to apply.' })}
                    </div>
                )
            }

            <Row>
                <Col md={6} xs={12}>
                    <h6 className={'mb-3'}>
                        {formatMessage({ id: 'access_codes.3114ae04', defaultMessage: 'Access Codes' })}
                    </h6>

                    <GMetrixAccordion>
                        <ChangeGroup
                            expanded
                            emptyText={formatMessage({ id: 'none_to_assign.95ebb90e', defaultMessage: 'None to assign.' })}
                            items={addedAccessCodes}
                            title={formatMessage({ id: 'to_assign.4a4db044', defaultMessage: 'To assign' })}
                            variant={'success'}
                        />

                        <ChangeGroup
                            emptyText={formatMessage({ id: 'none_to_unassign.c58ba01c', defaultMessage: 'None to unassign.' })}
                            items={removedAccessCodes}
                            title={formatMessage({ id: 'to_unassign.5b1ee12f', defaultMessage: 'To unassign' })}
                            variant={'danger'}
                        />

                        <ChangeGroup
                            emptyText={formatMessage({ id: 'none.cfb0e400', defaultMessage: 'None.' })}
                            items={keptAccessCodes}
                            title={formatMessage({ id: 'stays_assigned.78435fb0', defaultMessage: 'Stays assigned' })}
                            variant={'secondary'}
                        />
                    </GMetrixAccordion>
                </Col>

                <Col md={6} xs={12}>
                    <h6 className={'mb-3'}>
                        {formatMessage({ id: 'classrooms.e66138fb', defaultMessage: 'Classrooms' })}
                    </h6>

                    <GMetrixAccordion>
                        <ChangeGroup
                            expanded
                            emptyText={formatMessage({ id: 'none_to_assign.95ebb90e', defaultMessage: 'None to assign.' })}
                            items={addedClassrooms}
                            title={formatMessage({ id: 'to_assign.4a4db044', defaultMessage: 'To assign' })}
                            variant={'success'}
                        />

                        <ChangeGroup
                            emptyText={formatMessage({ id: 'none_to_unassign.c58ba01c', defaultMessage: 'None to unassign.' })}
                            items={removedClassrooms}
                            title={formatMessage({ id: 'to_unassign.5b1ee12f', defaultMessage: 'To unassign' })}
                            variant={'danger'}
                        />

                        <ChangeGroup
                            emptyText={formatMessage({ id: 'none.cfb0e400', defaultMessage: 'None.' })}
                            items={keptClassrooms}
                            title={formatMessage({ id: 'stays_assigned.78435fb0', defaultMessage: 'Stays assigned' })}
                            variant={'secondary'}
                        />
                    </GMetrixAccordion>
                </Col>
            </Row>

        </div>
    );

};
